import RiveComponent, { RiveAlignment } from '../../RiveComponent'
import RenderHTML from '../../ui-components/RenderHTML'
import { PrimaryButton } from '../PrimaryButton'
import { SecondaryButton } from '../SecondaryButton'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { trackEvent } from '../../../utils/appBridge'
import { EventNames } from '../../../types/misc.types'
import { useEffect } from 'react'
import { TerminalStatusContext } from '../../../types/mf-execution.types'

export const PartialSuccess = ({
  title,
  caption,
  ctas,
  statusContext
}: {
  title: string
  caption?: string
  ctas: { ctaType: 'primary' | 'secondary'; label: string; onClick: () => void }[]
  statusContext?: TerminalStatusContext
}) => {
  const getScreenName = () => {
    switch (statusContext) {
      case TerminalStatusContext.AUTO_PAY:
        return 'Autopay_setup_partial_success'
      default:
        return 'Order partial success'
    }
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: getScreenName(),
        eventId: `${statusContext}`
      }
    })
  }, [statusContext])

  return (
    <div
      style={{ paddingBottom: `calc(${getBottomPadding()}px + 8px)` }}
      className='flex flex-col h-full w-full relative bg-white px-5 pt-[35vh]'
    >
      {/* Animated Checkmark */}
      <div className='flex mt-3 h-12 w-12 mt-12'>
        <RiveComponent
          src={`/rives/mf-execution/check-blue.riv`}
          alignment={RiveAlignment.CENTER_LEFT}
          fallback={<PartialSuccessFallback />}
        />
      </div>

      {/* Text */}
      <div className='text-[28px] font-[500] mt-4 text-[#232323] leading-[150%] text-left animate-fade-150'>
        <RenderHTML innerHTMLText={title} />
      </div>

      {caption && (
        <div className='text-[14px] font-[400] text-[#6A6E6E] leading-[160%] text-left mt-3 animate-fade-150'>
          <RenderHTML innerHTMLText={caption} />
        </div>
      )}

      <div
        style={{ bottom: `${getBottomPadding() + 8}px` }}
        className='flex gap-4 w-full justify-center items-end absolute bottom-0 left-0 px-5 z-10'
      >
        {ctas.map((cta) =>
          cta.ctaType === 'primary' ? (
            <PrimaryButton
              key={cta.label}
              onClick={cta.onClick}
              label={cta.label}
              primaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
            />
          ) : (
            <SecondaryButton
              key={cta.label}
              onClick={cta.onClick}
              label={cta.label}
              secondaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
            />
          )
        )}
      </div>
    </div>
  )
}

export const PartialSuccessFallback = () => {
  return (
    <div className='flex justify-start h-max w-full mt-auto'>
      <Checkmark />
    </div>
  )
}

export const Checkmark = () => {
  const checkmarkStyles = {
    container: {
      width: '39px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    path: {
      strokeDasharray: '50',
      strokeDashoffset: '50',
      animation: 'drawPath 0.5s ease-in-out 0.1s forwards'
    }
  }

  return (
    <div style={checkmarkStyles.container}>
      <svg width='39' height='32' viewBox='16 20 32 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M20 32L28 40L44 24'
          stroke='#136FCC'
          strokeWidth='7'
          strokeLinecap='round'
          strokeLinejoin='round'
          fill='none'
          style={checkmarkStyles.path}
        />
      </svg>
      <style>
        {`
          @keyframes drawPath {
            0% {
              stroke-dashoffset: 50;
            }
            100% {
              stroke-dashoffset: 0;
            }
          }
        `}
      </style>
    </div>
  )
}
